import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../../lib/api";
import type { Configuration, Slot } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";
import { invalidateWorkspaceData } from "../workspace/invalidate";
import { toSlotsPayload, type DraftSlot } from "./slot-state";

/** Operator actions on configurations. Every success refreshes derived data. */
export function useConfigMutations(workspaceId: string) {
  const queryClient = useQueryClient();
  const refresh = () => invalidateWorkspaceData(queryClient, workspaceId);

  const create = useMutation({
    mutationFn: (name: string) =>
      apiRequest<Configuration>(paths.configurations(workspaceId), {
        method: "POST",
        body: { name },
      }),
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: ({
      configurationId,
      patch,
    }: {
      configurationId: string;
      patch: { name?: string; locked?: boolean };
    }) =>
      apiRequest<Configuration>(paths.configuration(workspaceId, configurationId), {
        method: "PATCH",
        body: patch,
      }),
    onSuccess: refresh,
  });

  const duplicate = useMutation({
    mutationFn: (configurationId: string) =>
      apiRequest<Configuration>(paths.configurationDuplicate(workspaceId, configurationId), {
        method: "POST",
      }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (configurationId: string) =>
      apiRequest<{ deleted: boolean }>(paths.configuration(workspaceId, configurationId), {
        method: "DELETE",
      }),
    onSuccess: refresh,
  });

  const restore = useMutation({
    mutationFn: (configurationId: string) =>
      apiRequest<{ restored: boolean }>(paths.configurationRestore(workspaceId, configurationId), {
        method: "POST",
      }),
    onSuccess: refresh,
  });

  return { create, update, duplicate, remove, restore };
}

/** Replaces every slot of a configuration in one batch (PUT slots). */
export function useSlotsSave(workspaceId: string, configurationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: readonly DraftSlot[]) =>
      apiRequest<Slot[]>(paths.slots(workspaceId, configurationId), {
        method: "PUT",
        body: toSlotsPayload(draft),
      }),
    onSuccess: () => invalidateWorkspaceData(queryClient, workspaceId),
  });
}
